/*
 * The company's own profile (Tenant/Company/CompanyController, CompanyResource),
 * as Demulla describes itself.
 */
import { company as demulla } from "@/mock/masters";
import { ok } from "./util";

const tenant = {
  code: demulla.code,
  display_name: demulla.name,
  legal_name: "Demulla Investment Limited",
  trading_name: demulla.name,
  industry: "Software",
  description: "Business software for East African organisations: DeeHr360 HR & Payroll, Demulla Loan Management and Enterprise Accounting.",
  employee_count: 48,
  website: "https://www.demulla.co.ke",
  registration_number: "PVT-2016/118204",
  kra_pin: "P051234567X",
  nssf_number: "NSSF-0091822",
  shif_number: "SHIF-4410192",
  nita_number: null,
  business_permit_number: "NCC/BP/2026/55810",
  contact_person_name: "Jane Wanjiku",
  contact_person_title: "Sales Manager",
  contact_email: "jane.wanjiku@demulla.co.ke",
  contact_phone: "+254 722 410 118",
  alternate_phone: "+254 20 444 1180",
  physical_address: "4th Floor, Westlands Square, Ring Road Westlands",
  postal_address: "P.O. Box 66210-00800, Nairobi",
  city: "Nairobi",
  region: "Nairobi County",
  country: demulla.country,
  latitude: -1.2648,
  longitude: 36.8045,
  logo_url: null,
  primary_color: "#22c55e",
  secondary_color: "#10b981",
};

const profile = {
  timezone: demulla.timezone,
  currency: demulla.currency,
  locale: "en_KE",
  date_format: "d M Y",
  financial_year_start_month: 7,
  planning_period: "quarterly",
  week_start_day: 1,
  email_notices: "important",
};

const completeness = () => {
  const t = tenant;
  const groups = {
    Identity: { "Legal name": t.legal_name, "Trading name": t.trading_name, Industry: t.industry, "Business description": t.description },
    Registration: {
      "Registration number": t.registration_number, "KRA PIN": t.kra_pin, "NSSF number": t.nssf_number,
      "SHIF number": t.shif_number, "NITA number": t.nita_number, "Business permit": t.business_permit_number,
    },
    Contact: { "Contact person": t.contact_person_name, Email: t.contact_email, Phone: t.contact_phone, Website: t.website },
    Address: { "Physical address": t.physical_address, "Postal address": t.postal_address, City: t.city, Country: t.country },
    Branding: { Logo: t.logo_url ? "Uploaded" : null, "Primary colour": t.primary_color },
  };

  let filled = 0;
  let total = 0;
  const sections = Object.entries(groups).map(([section, fields]) => {
    const missing = Object.keys(fields).filter((k) => fields[k] === null || fields[k] === "");
    const count = Object.keys(fields).length;
    filled += count - missing.length;
    total += count;
    return { section, filled: count - missing.length, total: count, missing };
  });

  return { percent: Math.round((filled / total) * 100), filled, total, sections };
};

const resource = () => ({
  code: tenant.code,
  display_name: tenant.display_name,
  legal_name: tenant.legal_name,
  trading_name: tenant.trading_name,
  industry: tenant.industry,
  description: tenant.description,
  employee_count: tenant.employee_count,
  website: tenant.website,
  registration: {
    registration_number: tenant.registration_number,
    kra_pin: tenant.kra_pin,
    nssf_number: tenant.nssf_number,
    shif_number: tenant.shif_number,
    nita_number: tenant.nita_number,
    business_permit_number: tenant.business_permit_number,
  },
  contact: {
    person_name: tenant.contact_person_name,
    person_title: tenant.contact_person_title,
    email: tenant.contact_email,
    phone: tenant.contact_phone,
    alternate_phone: tenant.alternate_phone,
  },
  location: {
    physical_address: tenant.physical_address,
    postal_address: tenant.postal_address,
    city: tenant.city,
    region: tenant.region,
    country: tenant.country,
    latitude: tenant.latitude,
    longitude: tenant.longitude,
  },
  branding: { logo_url: tenant.logo_url, primary_color: tenant.primary_color, secondary_color: tenant.secondary_color },
  completeness: completeness(),
  profile: { ...profile },
});

// Only what the request may change is taken, as the form request would.
const DETAIL_KEYS = Object.keys(tenant).filter((k) => !["code", "display_name", "legal_name", "logo_url", "primary_color", "secondary_color"].includes(k));

export default [
  ["get", "tenant/company", () => ok(resource())],

  ["put", "tenant/company/details", ({ body }) => {
    DETAIL_KEYS.forEach((k) => {
      if (k in body) tenant[k] = body[k];
    });
    if (body.trading_name) tenant.display_name = body.trading_name;
    return ok(resource(), "Company details updated");
  }],

  ["put", "tenant/company/profile", ({ body }) => {
    Object.keys(profile).forEach((k) => {
      if (k in body) profile[k] = body[k];
    });
    return ok(resource(), "Company settings updated");
  }],

  ["post", "tenant/company/branding", ({ body, config }) => {
    if (body.primary_color) tenant.primary_color = body.primary_color;
    if (body.secondary_color) tenant.secondary_color = body.secondary_color;

    // The chosen file is shown straight back from the browser's memory.
    const logo = typeof FormData !== "undefined" && config.data instanceof FormData ? config.data.get("logo") : null;
    if (logo && typeof URL !== "undefined" && URL.createObjectURL) tenant.logo_url = URL.createObjectURL(logo);

    return ok(
      resource(),
      logo
        ? "Logo saved to the Branding folder in the company documents. It appears once it has finished uploading."
        : "Branding updated",
    );
  }],
];
